export const GRAMS_PER_OUNCE = 28.3495

export interface PricingInputs {
  barWeightGrams: number
  targetMargin: number
  extraCostPerBatch: number
}

export interface PricingResult {
  batchTotalCost: number
  barsPerBatch: number
  costPerBar: number
  profitPerBar: number
  recommendedPrice: number
  roundedPrice: number
  wholesalePrice: number
  targetMargin: number
}

export const DEFAULT_PRICING_INPUTS: PricingInputs = {
  barWeightGrams: 100,
  targetMargin: 60,
  extraCostPerBatch: 0,
}

/**
 * Retail pricing from the batch cost.
 * Bar weight is kept in grams so the numbers stay stable when the display unit switches.
 */
export function calculatePricing(
  totalOilCost: number,
  totalBatchWeight: number,
  unit: "grams" | "ounces",
  inputs: PricingInputs
): PricingResult {
  const extra = Number.isFinite(inputs.extraCostPerBatch) ? Math.max(inputs.extraCostPerBatch, 0) : 0
  const batchTotalCost = Math.max(totalOilCost, 0) + extra

  const barWeightInUnit =
    inputs.barWeightGrams > 0
      ? unit === "grams"
        ? inputs.barWeightGrams
        : inputs.barWeightGrams / GRAMS_PER_OUNCE
      : 0

  const barsPerBatch = barWeightInUnit > 0 ? totalBatchWeight / barWeightInUnit : 0
  const costPerBar = barsPerBatch > 0 ? batchTotalCost / barsPerBatch : 0

  const targetMargin = Math.min(Math.max(inputs.targetMargin || 0, 0), 95)
  const recommendedPrice = costPerBar > 0 ? costPerBar / (1 - targetMargin / 100) : 0

  return {
    batchTotalCost,
    barsPerBatch,
    costPerBar,
    profitPerBar: recommendedPrice - costPerBar,
    recommendedPrice,
    roundedPrice: recommendedPrice > 0 ? Math.ceil(recommendedPrice * 2) / 2 : 0,
    wholesalePrice: recommendedPrice * 0.5,
    targetMargin,
  }
}

/** Bar weight in the display unit, for inputs and PDF output. */
export function barWeightForUnit(barWeightGrams: number, unit: "grams" | "ounces") {
  if (unit === "grams") return Math.round(barWeightGrams * 100) / 100
  return Math.round((barWeightGrams / GRAMS_PER_OUNCE) * 100) / 100
}
